/* Maharaja Super — zoom & fade transition for the Dawn slideshow.
   Active only when the slideshow section has class "mx-fx".
   Slides keep Dawn's side-by-side layout (so Dawn's own maths still works), but
   each one is shifted back onto the first position with a transform and shown
   or hidden by opacity. Arrows, dots, pause, autoplay, swipe and the theme
   editor are handled here instead of by scrolling. */
(function () {
  function init(root) {
    if (root.dataset.mxFxReady) return;
    const slider = root.querySelector('.slideshow');
    if (!slider) return;
    const slides = Array.from(slider.querySelectorAll('.slideshow__slide'));
    if (slides.length < 2) {
      root.classList.add('mx-fx-ready');
      if (slides[0]) slides[0].classList.add('is-active');
      return;
    }
    root.dataset.mxFxReady = 'true';

    const speed = (parseInt(root.dataset.mxSpeed, 10) || 5) * 1000;
    const fadeMs = parseInt(root.dataset.mxFade, 10) || 1000;
    let autoplay = root.dataset.mxAutoplay === 'true';
    let current = 0;
    let timer = null;
    let hoverPause = false;
    const leaveTimers = new Map();

    const dots = () => Array.from(root.querySelectorAll('.slider-counter__link'));
    const counter = root.querySelector('.slider-counter--current');
    const pauseBtns = Array.from(root.querySelectorAll('.slideshow__autoplay'));

    function place() {
      slides.forEach((slide) => {
        slide.style.transform = 'translateX(' + -slide.offsetLeft + 'px)';
      });
      slider.scrollLeft = 0;
    }

    function syncControls() {
      dots().forEach((dot, i) => {
        dot.classList.toggle('slider-counter__link--active', i === current);
        if (i === current) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (counter) counter.textContent = current + 1;
      slides.forEach((slide, i) => {
        const on = i === current;
        slide.setAttribute('aria-hidden', on ? 'false' : 'true');
        slide.querySelectorAll('a, button, input').forEach((el) => {
          if (on) el.removeAttribute('tabindex');
          else el.setAttribute('tabindex', '-1');
        });
      });
    }

    function show(index, first) {
      index = (index + slides.length) % slides.length;
      if (index === current && !first) return;
      const prev = slides[current];
      const next = slides[index];

      if (!first && prev !== next) {
        prev.classList.remove('is-active');
        prev.classList.add('is-leaving');
        clearTimeout(leaveTimers.get(prev));
        leaveTimers.set(
          prev,
          setTimeout(() => prev.classList.remove('is-leaving'), fadeMs + 50)
        );
      }

      next.classList.remove('is-leaving');
      // restart the slow zoom on the incoming slide
      next.classList.remove('is-active');
      void next.offsetWidth;
      next.classList.add('is-active');

      current = index;
      syncControls();
    }

    function stop() {
      clearInterval(timer);
      timer = null;
    }

    function start() {
      stop();
      if (!autoplay || hoverPause || document.hidden) return;
      timer = setInterval(() => show(current + 1), speed);
    }

    function setPaused(paused) {
      autoplay = !paused;
      pauseBtns.forEach((btn) => {
        btn.classList.toggle('slideshow__autoplay--paused', paused);
        btn.setAttribute('aria-label', paused ? 'Play slideshow' : 'Pause slideshow');
      });
      root.classList.toggle('mx-fx-paused', paused);
      start();
    }

    // Take over Dawn's buttons (capture phase runs before their own listeners).
    root.addEventListener(
      'click',
      (event) => {
        const target = event.target.closest('button');
        if (!target || !root.contains(target)) return;
        if (target.classList.contains('slider-button--next')) {
          event.preventDefault();
          event.stopPropagation();
          show(current + 1);
          start();
        } else if (target.classList.contains('slider-button--prev')) {
          event.preventDefault();
          event.stopPropagation();
          show(current - 1);
          start();
        } else if (target.classList.contains('slider-counter__link')) {
          event.preventDefault();
          event.stopPropagation();
          show(dots().indexOf(target));
          start();
        } else if (target.classList.contains('slideshow__autoplay')) {
          event.preventDefault();
          event.stopPropagation();
          setPaused(autoplay);
        }
      },
      true
    );

    // Swipe on touch screens
    let touchX = null;
    let touchY = null;
    slider.addEventListener(
      'touchstart',
      (event) => {
        touchX = event.touches[0].clientX;
        touchY = event.touches[0].clientY;
      },
      { passive: true }
    );
    slider.addEventListener(
      'touchend',
      (event) => {
        if (touchX === null) return;
        const dx = event.changedTouches[0].clientX - touchX;
        const dy = event.changedTouches[0].clientY - touchY;
        touchX = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          show(current + (dx < 0 ? 1 : -1));
          start();
        }
      },
      { passive: true }
    );

    // Pause while the mouse is over the slide or keyboard focus is inside
    root.addEventListener('mouseenter', () => {
      hoverPause = true;
      stop();
    });
    root.addEventListener('mouseleave', () => {
      hoverPause = false;
      start();
    });
    root.addEventListener('focusin', () => {
      hoverPause = true;
      stop();
    });
    root.addEventListener('focusout', (event) => {
      if (root.contains(event.relatedTarget)) return;
      hoverPause = false;
      start();
    });
    document.addEventListener('visibilitychange', start);

    // Theme editor: selecting a slide block shows it
    document.addEventListener('shopify:block:select', (event) => {
      const i = slides.indexOf(event.target.closest && event.target.closest('.slideshow__slide'));
      if (i > -1) {
        show(i);
        stop();
      }
    });
    document.addEventListener('shopify:block:deselect', start);

    // Dawn re-runs its own update on resize; put our state back afterwards.
    if (typeof root.update === 'function') {
      const dawnUpdate = root.update.bind(root);
      root.update = function () {
        try {
          dawnUpdate();
        } catch (e) {}
        syncControls();
      };
    }
    if ('ResizeObserver' in window) new ResizeObserver(place).observe(slider);
    window.addEventListener('resize', place);

    place();
    show(0, true);
    root.classList.add('mx-fx-ready');
    start();
  }

  function boot() {
    document.querySelectorAll('slideshow-component.mx-fx').forEach((root) => {
      if (window.customElements && !customElements.get('slideshow-component')) {
        customElements.whenDefined('slideshow-component').then(() => init(root));
      } else {
        init(root);
      }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
